import React, {useState, useCallback } from "react";
import { Box, Tabs, Tab, Paper, useMediaQuery, useTheme } from "@mui/material";
import Pending from "./Pending";
import Completed from "./Completed";
import Trades from "./Trades";
import useTouchInteraction from "../../hooks/useTouchInteraction";
const CACHE_KEY = "pending_orders_cache";

// Add TouchableTab component for better touch handling
const TouchableTab = React.memo(({ label, value, selected, onChange }) => {
  const handleClick = useCallback((e) => {
    if (!e) return;
    onChange(e, value);
  }, [onChange, value]);

  const { handlers, touchStates } = useTouchInteraction(handleClick, true);

  return (
    <Tab
      {...handlers}
      label={label}
      value={value}
      onClick={(e) => handleClick(e)}
      sx={{
        minHeight: "48px",
        height: "48px",
        textTransform: "none",
        fontWeight: selected ? 600 : 400,
        whiteSpace: "nowrap",
        fontSize: "0.875rem",
        minWidth: "auto",
        flex: 1,
        opacity: 1,
        color: selected ? 'primary.main' : 'text.secondary',
        transition: 'background-color 0.2s ease, color 0.2s ease',
        backgroundColor: touchStates.isActive && !touchStates.isTouchMoved ? 'action.hover' : 'transparent',
        WebkitTapHighlightColor: 'transparent',
        '&:active': {
          backgroundColor: !touchStates.isTouchMoved ? 'action.hover' : 'transparent'
        },
        '&.MuiTab-root': {
          flex: 1,
          maxWidth: 'none',
          padding: '0 16px'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: selected ? 'primary.main' : 'transparent',
          transition: 'background-color 0.2s ease'
        }
      }}
    />
  );
});

const Orders = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);
  const handleChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  React.useEffect(() => {
    return () => {
      localStorage.removeItem(CACHE_KEY);
    };
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        [theme.breakpoints.down('sm')]: {
          height: 'calc(100vh - 112px)',
          position: 'fixed',
          top: 56,
          left: 0,
          right: 0,
          bottom: 56
        }
      }}
    >
      <Paper 
        sx={{ 
          borderRadius: 0,
          position: 'relative',
          [theme.breakpoints.down('sm')]: {
            position: 'sticky',
            top: 0,
            zIndex: 1200,
            backgroundColor: 'background.paper'
          }
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChange}
          variant="fullWidth"
          sx={{
            boxShadow: "none",
            "& .MuiTabs-flexContainer": {
              height: "48px",
              width: '100%',
              display: 'flex'
            },
            "& .MuiTabs-scroller": {
              width: '100%'
            },
            "& .MuiTabs-indicator": {
              display: 'none'
            }
          }}
        >
          <TouchableTab
            label="Pending"
            value={0}
            selected={activeTab === 0}
            onChange={handleChange}
          />
          <TouchableTab
            label="Completed"
            value={1}
            selected={activeTab === 1}
            onChange={handleChange}
          />
          <TouchableTab
            label="Trades"
            value={2}
            selected={activeTab === 2}
            onChange={handleChange}
          />
        </Tabs>
      </Paper>
      <Box 
        sx={{ 
          flex: 1,
          height: isMobile ? 'calc(100% - 48px)' : 'calc(100vh - 48px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box 
          sx={{
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'none',
            p: { xs: 0.5, sm: 1 },
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            pb: isMobile ? '56px' : 0
          }}
        >
          {activeTab === 0 && <Pending />}
          {activeTab === 1 && <Completed />}
          {activeTab === 2 && <Trades />}
        </Box>
      </Box>
    </Box>
  );
};

export default Orders;
